import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * 交易类型状态映射 Hook
 * 用于将交易类型代码转换为对应的多语言文本
 */
export const useCardListStatus = () => {
  const { t } = useTranslation()

  const getCardListStatus = useCallback((type: string, defaultText?: string) => {
    switch (type) {
      case 'A':
        return t('授权')
      case 'D':
        return t('授权撤销')
      case 'C':
        return t('结算')
      case 'R':
        return t('退款')
      default:
        return defaultText || '-'
    }
  }, [t])

  return { getCardListStatus }
}


export const useRechargeListStatus = () => {
  const { t } = useTranslation()
  const getRechargeListStatus = useCallback((type: string, defaultText?: string) => {
    switch (type) {
      case 'Submitted':
        return t('已提交')
      case 'PendingScreening':
        return t('待审核')
      case 'PendingAuthorization':
        return t('待授权')
      case 'PendingSignature':
        return t('待签名')
      case 'Broadcasting':
        return t('广播中')
      case 'Confirming':
        return t('确认中')
      case 'Completed':
        return t('已完成')
      case 'Failed':
        return t('失败')
      case 'Rejected':
        return t('已拒绝')
      case 'Pending':
        return t('待处理')
      default:
        return defaultText || '-'
    }
  }, [t])

  return { getRechargeListStatus }
}

export const usePartnerLevelStatus = () => {
  const { t } = useTranslation()
  const getPartnerLevelStatus = useCallback((level: number) => {
    switch (level) {
      case 0:
        return t('普通会员')
      case 1:
        return t('普通合伙人')
      case 2:
        return t('VIP合伙人')
      case 3:
        return t('金卡合伙人')
      case 4:
        return t('钻石合伙人')
      case 5:
        return t('股东合伙人')
      case 6:
        return t('全球合伙人')
      case 7:
        return t('全球代理人')
      case 8:
        return t('全球运营商')
      default:
        return '-'
    }
  }, [t])

  return { getPartnerLevelStatus }
}
/**
 * 汇款状态映射 Hook
 * 用于将汇款状态代码转换为对应的多语言文本
 */
export const useRemittanceListStatus = () => {
  const { t } = useTranslation()
  const getRemittanceListStatus = useCallback((status: string, defaultText?: string) => {
    switch (status) {
      case 'pending':
        return t('处理中')
      case 'success':
        return t('成功')
      case 'failed':
        return t('失败')
      default:
        return defaultText || '-'
    }
  }, [t])

  return { getRemittanceListStatus }
}

/**
 * 职业类型枚举
 */
export enum OccupationType {
  SOE_AND_STATE_ORGAN_EXECUTIVES = 'SOE_AND_STATE_ORGAN_EXECUTIVES',
  SOE_AND_STATE_ORGAN_EMPLOYEES = 'SOE_AND_STATE_ORGAN_EMPLOYEES',
  PRIVATE_BUSINESS_OWNERS_AND_EXECUTIVES = 'PRIVATE_BUSINESS_OWNERS_AND_EXECUTIVES',
  PRIVATE_BUSINESS_EMPLOYEES = 'PRIVATE_BUSINESS_EMPLOYEES',
  NONEGOVERNMENT_ORGANIZATION_EXECUTIVES = 'NONEGOVERNMENT_ORGANIZATION_EXECUTIVES',
  NONEGOVERNMENT_ORGANIZATION_EMPLOYEES = 'NONEGOVERNMENT_ORGANIZATION_EMPLOYEES',
  SOLE_TRADERS = 'SOLE_TRADERS',
  RETIREES = 'RETIREES',
  STUDENTS = 'STUDENTS',
  UNEMPLOYED = 'UNEMPLOYED',
  FREELANCER = 'FREELANCER'
}

/**
 * 职业类型映射 Hook
 * 用于将职业类型代码转换为对应的多语言文本
 */
export const useOccupationListStatus = () => {
  const { t } = useTranslation()
  
  const getOccupationListStatus = useCallback((type: string, defaultText?: string) => {
    switch (type) {
      case OccupationType.SOE_AND_STATE_ORGAN_EXECUTIVES:
        return t('国有企事业单位高管')
      case OccupationType.SOE_AND_STATE_ORGAN_EMPLOYEES:
        return t('国有企事业单位员工')
      case OccupationType.PRIVATE_BUSINESS_OWNERS_AND_EXECUTIVES:
        return t('民营企业主及高管')
      case OccupationType.PRIVATE_BUSINESS_EMPLOYEES:
        return t('民营企业员工')
      case OccupationType.NONEGOVERNMENT_ORGANIZATION_EXECUTIVES:
        return t('非政府组织高管')
      case OccupationType.NONEGOVERNMENT_ORGANIZATION_EMPLOYEES:
        return t('非政府组织员工')
      case OccupationType.SOLE_TRADERS:
        return t('个体户')
      case OccupationType.RETIREES:
        return t('退休人员')
      case OccupationType.STUDENTS:
        return t('学生')
      case OccupationType.UNEMPLOYED:
        return t('无业人员')
      case OccupationType.FREELANCER:
        return t('自由职业者')
      default:
        return defaultText || '-'
    }
  }, [t])

  return { getOccupationListStatus }
}


export const useAllRecordListType = () => {
  const { t } = useTranslation()
  const getAllRecordListType = useCallback((type: string, defaultText?: string) => {
    switch (type) {
      case 'member_transfer_out':
        return t('站内转出');
      case 'member_transfer_in':
        return t('站内转入');
      case 'open_virtual_card_fee':
        return t('电子卡申请费');
      case 'open_physical_card_fee':
        return t('实体卡申请费');
      case 'card_auth':
        return t('电子卡授权');
      case 'card_auth_reverse':
        return t('电子卡撤销授权');
      case 'card_clearing':
        return t('电子卡结算');
      case 'card_clearing_refund':
        return t('电子卡退款');
      case 'bank_transfer':
        return t('汇款');
      case 'token_recharge':
        return t('存款代币');
      case 'exchange':
        return t('兑换');
      case 'claim':
        return t('领取');
      case 'auth_fee_refund':
        return t('授权费用超额退款');
      case 'card_recharge':
        return t('卡充值');
      case 'card_payout':
        return t('卡提现');
      case 'card_settlement':
        return t('卡结算');
      case 'card_close_refund':
        return t('卡退款');
      case 'authentication_approved':
        return t('卡授权');
      default:
        return defaultText || '-'
    }
  }, [t])

  return { getAllRecordListType }
}

/**
 * 卡片交易状态 Hook
 * 基于 TransactionStatus 枚举
 */
export const useTransactionStatus = () => {
  const { t } = useTranslation()

  const getTransactionStatus = useCallback((status: string, defaultText?: string, txnTypeString?: string) => {
    switch (status) {
      case 'authentication_approved':
        return t('授权')
      case 'authentication_declined':
        return t('授权失败')
      case 'authentication_reversal_settled':
        return t('授权取消')
      case 'authentication_settled':
        return t('结算')
      case 'refund_approved':
        return t('退款授权')
      case 'refund_settled':
        return t('退款结算')
      case 'deposit':
        return t('充值')
      case 'withdrawal':
        return t('提现至{{currency}}账户', { currency: (txnTypeString || 'USD').toUpperCase() })
      default:
        return defaultText || '-'
    }
  }, [t])

  return { getTransactionStatus }
}